import React, { useEffect, useState } from 'react';
import { EMAIL, photos, socials } from '../data';
import './Header.css';

const menu = [
  { href: '#/', label: 'Home', id: 'home' },
  { href: '#/about', label: 'About', id: 'about' },
  { href: '#/work', label: 'Work', id: 'work' },
  { href: '#/contact', label: 'Contact', id: 'contact' },
];

function useTheme() {
  const [light, setLight] = useState(() => {
    try {
      return localStorage.getItem('theme') === 'light';
    } catch (e) {
      return false;
    }
  });
  useEffect(() => {
    document.documentElement.classList.toggle('light', light);
    document.querySelector('meta[name="theme-color"]')?.setAttribute('content', light ? '#f5f5f5' : '#111111');
    try {
      localStorage.setItem('theme', light ? 'light' : 'dark');
    } catch (e) {}
  }, [light]);
  return [light, () => setLight(l => !l)];
}

export default function Header({ page }) {
  const [open, setOpen] = useState(false);
  const [light, toggleTheme] = useTheme();
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // The menu closes on Escape, and whenever the page changes
  useEffect(() => setOpen(false), [page]);
  useEffect(() => {
    if (!open) return undefined;
    const onKey = e => e.key === 'Escape' && setOpen(false);
    document.addEventListener('keydown', onKey);
    document.documentElement.classList.add('menu-open');
    return () => {
      document.removeEventListener('keydown', onKey);
      document.documentElement.classList.remove('menu-open');
    };
  }, [open]);

  const active = ['project', 'engineer', 'design'].includes(page) ? 'work' : page;

  return (
    <>
      <header className={`hdr ${scrolled && !open ? 'scrolled' : ''}`}>
        <div className="shell hdr-in">
          <a className="hdr-logo" href="#/" aria-label="Home">
            <img src={photos.portrait} alt="" />
            <span className="display">Topseen</span>
          </a>
          <div className="hdr-right">
            {!open && (
              <button type="button" className="hdr-theme" onClick={toggleTheme} aria-label={light ? 'Switch to dark mode' : 'Switch to light mode'}>
                {light ? (
                  <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M20 14.5A8 8 0 0 1 9.5 4a8 8 0 1 0 10.5 10.5Z" /></svg>
                ) : (
                  <svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="12" r="4" /><path d="M12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4" /></svg>
                )}
              </button>
            )}
            <button type="button" className={`hdr-menu ${open ? 'open' : ''}`} onClick={() => setOpen(o => !o)} aria-expanded={open}>
              <span className="meta">{open ? 'Close' : 'Index'}</span>
              <span className="burger" aria-hidden="true"><i /><i /><i /></span>
            </button>
          </div>
        </div>
      </header>

      <div className={`menu ${open ? 'open' : ''}`} aria-hidden={!open}>
        <nav className="shell menu-list" aria-label="Pages">
          {menu.map((m, i) => (
            <a key={m.id} href={m.href} className={active === m.id ? 'on' : ''} style={{ '--d': i }} tabIndex={open ? 0 : -1}>
              <span className="meta menu-num">{String(i + 1).padStart(2, '0')}</span>
              <span className="mask"><span className="display menu-word">{m.label}</span></span>
              <span className="menu-dot" aria-hidden="true" />
            </a>
          ))}
        </nav>
        <div className="shell menu-foot">
          <div>
            <p className="meta menu-label">Get in touch</p>
            <a className="menu-email" href={`mailto:${EMAIL}`} tabIndex={open ? 0 : -1}>{EMAIL}</a>
          </div>
          <div className="menu-links">
            {socials.map(s => (
              <a key={s.label} className="meta" href={s.url} target="_blank" rel="noreferrer" tabIndex={open ? 0 : -1}>
                {s.label.replace(' / Twitter', '')} ↗
              </a>
            ))}
          </div>
        </div>
      </div>
    </>
  );
}
